import {useDrawingStore} from '@/store/drawing'
import Konva from "konva";

interface getMapSnapshotProps {
  selectedItem: any | null | undefined
  returnBlob?: boolean // Optional prop to determine return type
}

const updateDrawingMapOpacityPdfLayer = (stage: Konva.Stage, newOpacity: number) => {
  // Find the layer by ID within the stage
  const pdfImgLayer = stage.findOne('#pdfImgLayer') as Konva.Layer

  if (!pdfImgLayer) {
    console.log('pdfImgLayer not found in stage')
    return
  }

  // Set new opacity filter value
  pdfImgLayer.opacity(newOpacity / 100)
}

// Dynamic scale calculation based on dimension size
const calculateOptimalScale = (dimensionSize: number): number => {
  // Base scaling parameters
  const baseSize = 25

  // Define scaling -> smaller dimensionSize = higher baseScale
  const getBaseScale = (size: number): number => {
    if (size <= 1) return 10
    if (size <= 2) return 8
    if (size <= 3) return 6
    if (size <= 4) return 5
    if (size <= 5) return 4
    if (size <= 15) return 3
    return 2 // For sizes > 15
  }

  const baseScale = getBaseScale(dimensionSize)

  // As dimensionSize increases, scale decreases
  const inverseFactor = baseSize / dimensionSize

  // Apply smoothing with logarithmic scaling
  const smoothFactor = Math.log(inverseFactor + 1)

  const dynamicScale = baseScale * smoothFactor

  return Math.max(0.1, Math.min(100, dynamicScale))
}

export const getMapSnapshot = async ({
  selectedItem,
  returnBlob = false
}: getMapSnapshotProps): Promise<HTMLImageElement | Blob | null> => {
  const drawingStore = useDrawingStore()

  if (!drawingStore.stage || !drawingStore.stage.getStage()) {
    console.log('Stage not found')
    return null
  }

  if (!selectedItem?.id) {
    console.log('No selected items for Snapshot')
    return null
  }

  try {
    // Store original state for restoration
    const stage = drawingStore.stage.getStage()
    const originalScale = JSON.parse(JSON.stringify(drawingStore.scale.x))
    const originalPosition = {
      x: JSON.parse(JSON.stringify(stage.x())),
      y: JSON.parse(JSON.stringify(stage.y()))
    }
    const originalOpacity = JSON.parse(JSON.stringify(drawingStore.colorFilters.opacity))

    // Hide all sign items except the specific one when printing
    drawingStore.updateSpecificItemIdToShow(selectedItem.id)

    // Calculate the max dimension size of the sign
    const maxDimension = Math.max(selectedItem?.width || 25, selectedItem?.height || 25)
    const maxScale = Math.max(selectedItem?.scaleX || 1, selectedItem?.scaleY || 1)
    const maxDimensionSize = maxDimension * maxScale

    // Set opacity for PDF layer
    updateDrawingMapOpacityPdfLayer(stage, 50)

    // Dynamic scale calculation based on dimension size
    const optimalScale = calculateOptimalScale(maxDimensionSize)

    // Apply optimal scale to stage
    stage.scale({ x: optimalScale, y: optimalScale })

    await nextTick()

    // Calculate sign dimensions with optimal scale
    const imgWidth = (selectedItem?.width || 25) * (selectedItem?.scaleX || 1)
    const imgHeight = (selectedItem?.height || 25) * (selectedItem?.scaleY || 1)

    // Calculate snapshot dimensions - 12 times larger than sign for context
    const snapshotWidth = maxDimensionSize * 12 * optimalScale
    const snapshotHeight = maxDimensionSize * 12 * optimalScale

    // Calculate the visual center considering rotation
    const rotation = selectedItem.rotation || 0
    const angleInRadians = rotation * Math.PI / 180

    // Center of the image in local coordinates
    const localCenterX = imgWidth / 2
    const localCenterY = imgHeight / 2

    // Apply rotation transformation to find actual center in stage coordinates
    const cos = Math.cos(angleInRadians)
    const sin = Math.sin(angleInRadians)

    // Transform local center to stage coordinates
    const signCenterX = selectedItem.x + localCenterX * cos - localCenterY * sin
    const signCenterY = selectedItem.y + localCenterX * sin + localCenterY * cos

    // Calculate snapshot area position to center on the sign
    const snapshotX = (signCenterX * optimalScale + originalPosition.x) - (snapshotWidth / 2)
    const snapshotY = (signCenterY * optimalScale + originalPosition.y) - (snapshotHeight / 2)

    // Get the data URL of the stage with calculated dimensions
    const dataUrl = stage.toDataURL({
      x: snapshotX,
      y: snapshotY,
      width: snapshotWidth,
      height: snapshotHeight,
      quality: 1,
      pixelRatio: 2,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: 'high'
    })

    // Restore original state
    await nextTick(() => {
      drawingStore.updateSpecificItemIdToShow(null)
      updateDrawingMapOpacityPdfLayer(stage, originalOpacity)
      stage.scale({ x: originalScale, y: originalScale })
      stage.position({ x: originalPosition.x, y: originalPosition.y })
    })

    if (returnBlob) {
      // Convert the data URL to a Blob
      return await fetch(dataUrl).then((res) => res.blob())
    } else {
      // Return an HTMLImageElement
      const mapSnapshot: HTMLImageElement = new Image()
      mapSnapshot.crossOrigin = 'Anonymous'
      mapSnapshot.src = dataUrl

      // Set dimensions while maintaining aspect ratio
      mapSnapshot.width = snapshotWidth
      mapSnapshot.height = snapshotHeight

      // Apply CSS to control the display size
      mapSnapshot.style.maxWidth = '300px' // Limit maximum width for display
      mapSnapshot.style.maxHeight = '200px' // Limit maximum height for display
      mapSnapshot.style.width = 'auto'
      mapSnapshot.style.height = 'auto'
      mapSnapshot.style.objectFit = 'contain'

      return mapSnapshot
    }
  } catch (e) {
    console.log(e)
    return null
  }
}
